import { useQuery } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import { CardPreview, type CardPreviewData } from '@bb/ui';
import { ApiError, type AdminApi, type PayloadPreview, type PublicPreview } from '../api/client.ts';
import { HumanView } from '../features/composer/shared/PayloadPreviewPanel.tsx';
import { CHANNEL_LABELS } from '../features/composer/shared/personalization.ts';
import { utcTime } from '../features/composer/shared/PreviewLinksSection.tsx';

const CARD_TYPES: ReadonlyArray<CardPreviewData['type']> = ['image_only', 'captioned_image', 'classic'];

/** The SDK card of a rendered Content Card, when it has the shape the feed preview draws. */
function cardOf(payload: unknown): CardPreviewData | null {
  if (!payload || typeof payload !== 'object') return null;
  const card = payload as Partial<CardPreviewData>;
  return card.type && CARD_TYPES.includes(card.type) ? { ...card, type: card.type, pinned: Boolean(card.pinned), dismissible: Boolean(card.dismissible) } : null;
}

function PreviewBody({ preview }: { preview: PublicPreview }) {
  const card = preview.channel === 'content_card' && !preview.aborted ? cardOf(preview.payload) : null;
  // The public answer has no user: the composer's view needs only the rendered part.
  const result: PayloadPreview = { ...preview, profileId: '', unresolved: [], usedDefaults: [] } as unknown as PayloadPreview;
  return (
    <>
      <p className="hint">
        {CHANNEL_LABELS[preview.channel]} in {preview.locale}. The link works until {utcTime(preview.expiresAt)}.
      </p>
      {card ? (
        <div className="public-preview__card">
          <CardPreview card={card} platform="android" />
        </div>
      ) : (
        <HumanView channel={preview.channel} result={result} />
      )}
      <p className="hint">Rendered when the link was created: later changes to the message do not show here.</p>
    </>
  );
}

/** `/preview/<token>` (BB-59): a message preview colleagues open without signing in. */
export function PublicPreviewPage({ token }: { token: string }) {
  const { api } = useRouter().options.context as { api: AdminApi };
  const preview = useQuery({ queryKey: ['public-preview', token], queryFn: () => api.publicPreview(token), retry: false });
  const missing = preview.error instanceof ApiError && preview.error.status === 404;
  return (
    <main className="public-preview">
      <title>Message preview</title>
      <meta name="robots" content="noindex, nofollow" />
      <meta name="referrer" content="no-referrer" />
      <h1>Message preview</h1>
      {preview.isPending && <p aria-busy="true">Loading the preview…</p>}
      {preview.isError && <p role="alert">{missing ? 'This preview link is missing or has expired.' : 'The preview could not be loaded: try again later.'}</p>}
      {preview.data && <PreviewBody preview={preview.data} />}
    </main>
  );
}
